import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {Section} from 'constants/NavConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderDAO from 'file-browser/FolderDAO';
import AppDispatcher from 'lib/Dispatcher';
import {getStore} from 'redux-stores/Store';
import {InventoryRegistryDataModAction} from 'samples/getInventoryRegistryChangeSummary';
import {forceRefreshSearch} from 'search/SearchUIReduxActions';
import {patchJSONPromise, postJSONPromise} from 'util/Net';

export interface CreateCanvasFolderArtifactParams {
  name: string;
  folderId: string;
  canvasVersion: string;
  description?: string;
}

export interface CreateCanvasFolderArtifactResponse {
  createdIds: Array<string>;
}

export interface UpdateCanvasFolderArtifactParams {
  id: string;
  name: string;
  description: string | null;
}

export interface UpdateCanvasFolderArtifactResponse {
  updatedIds: Array<string>;
}

export async function createCanvasFolderArtifact(
  artifact: CreateCanvasFolderArtifactParams
): Promise<CreateCanvasFolderArtifactResponse> {
  const creates = [artifact];
  const response: CreateCanvasFolderArtifactResponse = await postJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.CANVAS_FOLDER_ARTIFACT),
    data: {creates},
  });
  getStore().dispatch(forceRefreshSearch(Section.SEARCH));
  return response;
}

export async function updateCanvasFolderArtifact(
  artifact: UpdateCanvasFolderArtifactParams
): Promise<UpdateCanvasFolderArtifactResponse> {
  const updates = [artifact];
  const response: UpdateCanvasFolderArtifactResponse = await patchJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.CANVAS_FOLDER_ARTIFACT),
    data: {updates},
  });
  getStore().dispatch(forceRefreshSearch(Section.SEARCH));
  return response;
}

export interface MoveCanvasFolderArtifactParams {
  id: string;
  fromFolderId: string;
  toFolderId: string;
}

export function moveCanvasFolderArtifact({
  id,
  fromFolderId,
  toFolderId,
}: MoveCanvasFolderArtifactParams): Promise<boolean> {
  return callAsyncWithDataModifications({
    changeSummary: 'Moving 1 item',
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      await FileBrowserDAO.moveFiles([id], toFolderId, {parentFolderIds: [fromFolderId]}, dataModsHeader);
      const folder = await FolderDAO.fetch(toFolderId, {summary: '1'});
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED,
        ids: [id],
        folder,
      });
    },
    analyticsParams: {
      module: 'NAVIGATION',
      actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
      modifiedAction: InventoryRegistryDataModAction.MODIFY,
      numModifiedTotalItems: 1,
      entryId: null,
    },
    apiIds: [id],
    useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS,
    useFolderMoveDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_PROJECTS_FOLDERS
      ? {apiIds: [id], destinationFolderApiId: toFolderId}
      : undefined,
  });
}
